import { ThunkDispatch } from "core-integration/src/store";
import { getEvents } from "core-integration/src/store/events/reducers";
import { getRules } from "core-integration/src/store/game/reducers";
import { getCreateClassicLeagueError } from "core-integration/src/store/leagues/reducers";
import { createClassicLeague } from "core-integration/src/store/leagues/thunks";
import { ICreateClassicLeagueData } from "core-integration/src/store/leagues/types";
import React, { useState } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import Button from "../Button";
import Collapsible from "../Collapsible";
import Copy from "../Copy";
import { FieldWrap, InputField, SelectField } from "../FieldRenderers";
import { Main, Wrapper } from "../Layout";
import SubHeading from "../SubHeading";
import Title from "../Title";

type Props = WithTranslation;

interface IState {
  name: string;
  startEvent: number;
}

const CreateClassic = ({ t }: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useNavigate();
  const [state, setState] = useState<IState>({
    name: "",
    startEvent: 1,
  });

  const error = useSelector(getCreateClassicLeagueError);
  const events = useSelector(getEvents);
  const rules = useSelector(getRules);

  const requiredFields: Array<keyof IState> = ["name", "startEvent"];

  const apiDataFromState = (): ICreateClassicLeagueData => ({
    name: state.name,
    start_event: state.startEvent,
  });

  const handleNameChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, name: e.currentTarget.value });

  const handleStartEventChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, startEvent: parseInt(e.currentTarget.value, 10) });

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    await dispatch(createClassicLeague(apiDataFromState()));
    navigate("/leagues");
  };

  const disabled = requiredFields.some((e) => !state[e]);
  const lastEventRound =
    events?.length > 0 ? events[events.length - 1].id : null;
  return (
    <Wrapper>
      <Main>
        <Copy>
          <Title>
            {t("createClassic.title", "Create a new classic league & cup")}
          </Title>
          {error ? (
            error.badRequest &&
            error.badRequest.non_field_errors &&
            error.badRequest.non_field_errors[0].code ===
              "max_private_entries" ? (
              <p>
                {t(
                  "createClassic.error",
                  "You may only enter {{ maxPrivateEntries }} private leagues",
                  {
                    maxPrivateEntries: rules && rules.league_join_private_max,
                  },
                )}
              </p>
            ) : (
              <p>{JSON.stringify(error)}</p>
            )
          ) : (
            ""
          )}
          <form onSubmit={handleSubmit}>
            <FieldWrap>
              <InputField
                id="ismLeagueName"
                label={t("createClassic.leagueName.label", "League name")}
                value={state.name}
                onChange={handleNameChange}
                hint={t(
                  "createClassic.leagueName.hint",
                  "Maximum 30 characters",
                )}
                maxLength={30}
              />
            </FieldWrap>
            <FieldWrap>
              <SelectField
                id="ismLeagueStartEvent"
                value={state.startEvent}
                onChange={handleStartEventChange}
                label={t("createClassic.startEventLabel", "Scoring starts")}
              >
                {events.map((e) => (
                  <option
                    value={e.id}
                    aria-selected={e.id === state.startEvent}
                    key={e.id}
                  >
                    {e.name}
                  </option>
                ))}
              </SelectField>
            </FieldWrap>
            <FieldWrap>
              <Button type="submit" disabled={disabled}>
                {t("createClassic.button", "Create league")}
              </Button>
            </FieldWrap>
            <SubHeading>{t("createClassic.theCup", "The Cup")}</SubHeading>
            <p>
              {t(
                "createClassic.theCupExplain",
                "League cups are now included in Private Classic Leagues. The League Cups will start in the second half of the season dependent on the size of your league and will conclude in Round {{ round }}.",
                { round: lastEventRound },
              )}
            </p>
            <Collapsible
              headingText={t("createClassic.theCupHow", "How the cup works")}
            >
              <p>
                {t(
                  "createClassic.theCupHowExplain",
                  "In the first round of the cup each qualifying team will be randomly drawn against another.  The winner (the team with the highest Round score minus any transfer points), will progress to the second round of the cup and another random draw.  The loser will be eliminated from the cup.  This process continues until the final round of the cup when there will only be two teams remaining.",
                )}
              </p>
              <p>
                {t(
                  "createClassic.theCupHowExplain1",
                  "In the case of a draw, then the following tie-breaks will be applied until a winner is found:",
                )}
              </p>
              <ol>
                <li>
                  {t(
                    "createClassic.theCupHowExplain2",
                    "Most goals scored in the round",
                  )}
                </li>
                <li>
                  {t(
                    "createClassic.theCupHowExplain3",
                    "Fewest goals conceded in the round",
                  )}
                </li>
                <li>
                  {t("createClassic.theCupHowExplain4", "Virtual coin toss")}
                </li>
              </ol>
            </Collapsible>
          </form>
        </Copy>
      </Main>
    </Wrapper>
  );
};

export default withTranslation()(CreateClassic);
